/**
 * Lightweight SVG icon set (stroke-based, inherits currentColor).
 * No external dependency — keeps the bundle small.
 */

interface IconProps {
  size?: number;
  className?: string;
  strokeWidth?: number;
  style?: React.CSSProperties;
}

const base = (size: number, className?: string, style?: React.CSSProperties) => ({
  width: size,
  height: size,
  viewBox: "0 0 24 24",
  fill: "none",
  stroke: "currentColor",
  strokeWidth: 2,
  strokeLinecap: "round" as const,
  strokeLinejoin: "round" as const,
  className,
  style,
});

export const IconDashboard = ({ size = 20, className, style }: IconProps) => (
  <svg {...base(size, className, style)}><rect x="3" y="3" width="7" height="9" rx="1"/><rect x="14" y="3" width="7" height="5" rx="1"/><rect x="14" y="12" width="7" height="9" rx="1"/><rect x="3" y="16" width="7" height="5" rx="1"/></svg>
);

export const IconBranch = ({ size = 20, className, style }: IconProps) => (
  <svg {...base(size, className, style)}><path d="M3 21h18"/><path d="M5 21V7l8-4v18"/><path d="M19 21V11l-6-4"/><path d="M9 9v.01"/><path d="M9 12v.01"/><path d="M9 15v.01"/><path d="M9 18v.01"/></svg>
);

export const IconWarehouse = ({ size = 20, className, style }: IconProps) => (
  <svg {...base(size, className, style)}><path d="M3 21V7l9-4 9 4v14"/><path d="M3 21h18"/><path d="M9 21v-6h6v6"/><path d="M9 11h.01"/><path d="M15 11h.01"/></svg>
);

export const IconCatalog = ({ size = 20, className, style }: IconProps) => (
  <svg {...base(size, className, style)}><path d="M4 4h16v16H4z"/><path d="M4 9h16"/><path d="M9 9v11"/></svg>
);

export const IconOrders = ({ size = 20, className, style }: IconProps) => (
  <svg {...base(size, className, style)}><path d="M6 2L3 6v14a2 2 0 002 2h14a2 2 0 002-2V6l-3-4z"/><path d="M3 6h18"/><path d="M16 10a4 4 0 01-8 0"/></svg>
);

export const IconCustomers = ({ size = 20, className, style }: IconProps) => (
  <svg {...base(size, className, style)}><path d="M17 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 00-3-3.87"/><path d="M16 3.13a4 4 0 010 7.75"/></svg>
);

export const IconStaff = ({ size = 20, className, style }: IconProps) => (
  <svg {...base(size, className, style)}><rect x="2" y="7" width="20" height="14" rx="2"/><path d="M16 21V5a2 2 0 00-2-2h-4a2 2 0 00-2 2v16"/><path d="M2 13h20"/></svg>
);

export const IconDevice = ({ size = 20, className, style }: IconProps) => (
  <svg {...base(size, className, style)}><rect x="5" y="2" width="14" height="20" rx="2"/><path d="M12 18h.01"/></svg>
);

export const IconBuilding = ({ size = 20, className, style }: IconProps) => (
  <svg {...base(size, className, style)}><rect x="4" y="2" width="16" height="20" rx="2"/><path d="M9 22v-4h6v4"/><path d="M8 6h.01"/><path d="M16 6h.01"/><path d="M12 6h.01"/><path d="M12 10h.01"/><path d="M12 14h.01"/><path d="M16 10h.01"/><path d="M16 14h.01"/><path d="M8 10h.01"/><path d="M8 14h.01"/></svg>
);

export const IconReceipt = ({ size = 20, className, style }: IconProps) => (
  <svg {...base(size, className, style)}><path d="M4 2v20l2-1 2 1 2-1 2 1 2-1 2 1 2-1 2 1V2l-2 1-2-1-2 1-2-1-2 1-2-1-2 1z"/><path d="M8 7h8"/><path d="M8 11h8"/><path d="M8 15h5"/></svg>
);

export const IconChart = ({ size = 20, className, style }: IconProps) => (
  <svg {...base(size, className, style)}><path d="M3 3v18h18"/><path d="M18 17V9"/><path d="M13 17V5"/><path d="M8 17v-3"/></svg>
);

export const IconLogout = ({ size = 20, className, style }: IconProps) => (
  <svg {...base(size, className, style)}><path d="M9 21H5a2 2 0 01-2-2V5a2 2 0 012-2h4"/><path d="M16 17l5-5-5-5"/><path d="M21 12H9"/></svg>
);

export const IconSearch = ({ size = 20, className, style }: IconProps) => (
  <svg {...base(size, className, style)}><circle cx="11" cy="11" r="8"/><path d="M21 21l-4.35-4.35"/></svg>
);

export const IconCheck = ({ size = 20, className, style }: IconProps) => (
  <svg {...base(size, className, style)}><path d="M20 6L9 17l-5-5"/></svg>
);

export const IconX = ({ size = 20, className, style }: IconProps) => (
  <svg {...base(size, className, style)}><path d="M18 6L6 18"/><path d="M6 6l12 12"/></svg>
);

export const IconMenu = ({ size = 20, className, style }: IconProps) => (
  <svg {...base(size, className, style)}><path d="M4 6h16"/><path d="M4 12h16"/><path d="M4 18h16"/></svg>
);

export const IconAlert = ({ size = 20, className, style }: IconProps) => (
  <svg {...base(size, className, style)}><path d="M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z"/><path d="M12 9v4"/><path d="M12 17h.01"/></svg>
);

export const IconLock = ({ size = 20, className, style }: IconProps) => (
  <svg {...base(size, className, style)}><rect x="3" y="11" width="18" height="11" rx="2"/><path d="M7 11V7a5 5 0 0110 0v4"/></svg>
);

export const IconMail = ({ size = 20, className, style }: IconProps) => (
  <svg {...base(size, className, style)}><rect x="2" y="4" width="20" height="16" rx="2"/><path d="M22 7l-10 5L2 7"/></svg>
);

export const IconPhone = ({ size = 20, className, style }: IconProps) => (
  <svg {...base(size, className, style)}><path d="M22 16.92v3a2 2 0 01-2.18 2 19.79 19.79 0 01-8.63-3.07 19.5 19.5 0 01-6-6 19.79 19.79 0 01-3.07-8.67A2 2 0 014.11 2h3a2 2 0 012 1.72c.127.96.361 1.903.7 2.81a2 2 0 01-.45 2.11L8.09 9.91a16 16 0 006 6l1.27-1.27a2 2 0 012.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0122 16.92z"/></svg>
);

export const IconPlus = ({ size = 20, className, style }: IconProps) => (
  <svg {...base(size, className, style)}><path d="M12 5v14"/><path d="M5 12h14"/></svg>
);

export const IconEdit = ({ size = 20, className, style }: IconProps) => (
  <svg {...base(size, className, style)}><path d="M11 4H4a2 2 0 00-2 2v14a2 2 0 002 2h14a2 2 0 002-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 013 3L12 15l-4 1 1-4 9.5-9.5z"/></svg>
);

export const IconTrash = ({ size = 20, className, style }: IconProps) => (
  <svg {...base(size, className, style)}><path d="M3 6h18"/><path d="M19 6v14a2 2 0 01-2 2H7a2 2 0 01-2-2V6"/><path d="M8 6V4a2 2 0 012-2h4a2 2 0 012 2v2"/></svg>
);

export const IconCalendar = ({ size = 20, className, style }: IconProps) => (
  <svg {...base(size, className, style)}><rect x="3" y="4" width="18" height="18" rx="2"/><path d="M16 2v4"/><path d="M8 2v4"/><path d="M3 10h18"/></svg>
);

export const IconClock = ({ size = 20, className, style }: IconProps) => (
  <svg {...base(size, className, style)}><circle cx="12" cy="12" r="10"/><path d="M12 6v6l4 2"/></svg>
);

export const IconTrending = ({ size = 20, className, style }: IconProps) => (
  <svg {...base(size, className, style)}><path d="M23 6l-9.5 9.5-5-5L1 18"/><path d="M17 6h6v6"/></svg>
);

export const IconCreditCard = ({ size = 20, className, style }: IconProps) => (
  <svg {...base(size, className, style)}><rect x="1" y="4" width="22" height="16" rx="2"/><path d="M1 10h22"/></svg>
);

export const IconCash = ({ size = 20, className, style }: IconProps) => (
  <svg {...base(size, className, style)}><rect x="2" y="6" width="20" height="12" rx="2"/><path d="M6 12h.01"/><circle cx="12" cy="12" r="2"/></svg>
);

export const IconChevronLeft = ({ size = 20, className, style }: IconProps) => (
  <svg {...base(size, className, style)}><path d="M15 18l-6-6 6-6"/></svg>
);

export const IconLoader = ({ size = 20, className, style }: IconProps) => (
  <svg {...base(size, className, style)} className={className ? `${className} spin` : "spin"}><path d="M21 12a9 9 0 11-6.219-8.56"/></svg>
);

export const IconSparkle = ({ size = 20, className, style }: IconProps) => (
  <svg {...base(size, className, style)}><path d="M12 3l1.9 5.8a2 2 0 001.3 1.3l5.8 1.9-5.8 1.9a2 2 0 00-1.3 1.3L12 21l-1.9-5.8a2 2 0 00-1.3-1.3L3 12l5.8-1.9a2 2 0 001.3-1.3L12 3z"/></svg>
);

export const IconSettings = ({ size = 20, className, style }: IconProps) => (
  <svg {...base(size, className, style)}><circle cx="12" cy="12" r="3"/><path d="M19.4 15a1.65 1.65 0 00.33 1.82l.06.06a2 2 0 010 2.83 2 2 0 01-2.83 0l-.06-.06a1.65 1.65 0 00-1.82-.33 1.65 1.65 0 00-1 1.51V21a2 2 0 01-2 2 2 2 0 01-2-2v-.09A1.65 1.65 0 009 19.4a1.65 1.65 0 00-1.82.33l-.06.06a2 2 0 01-2.83 0 2 2 0 010-2.83l.06-.06a1.65 1.65 0 00.33-1.82 1.65 1.65 0 00-1.51-1H3a2 2 0 01-2-2 2 2 0 012-2h.09A1.65 1.65 0 004.6 9a1.65 1.65 0 00-.33-1.82l-.06-.06a2 2 0 010-2.83 2 2 0 012.83 0l.06.06a1.65 1.65 0 001.82.33H9a1.65 1.65 0 001-1.51V3a2 2 0 012-2 2 2 0 012 2v.09a1.65 1.65 0 001 1.51 1.65 1.65 0 001.82-.33l.06-.06a2 2 0 012.83 0 2 2 0 010 2.83l-.06.06a1.65 1.65 0 00-.33 1.82V9a1.65 1.65 0 001.51 1H21a2 2 0 012 2 2 2 0 01-2 2h-.09a1.65 1.65 0 00-1.51 1z"/></svg>
);

export const IconGlobe = ({ size = 20, className, style }: IconProps) => (
  <svg {...base(size, className, style)}><circle cx="12" cy="12" r="9"/><path d="M3 12h18"/><path d="M12 3a15 15 0 014 9 15 15 0 01-4 9 15 15 0 01-4-9 15 15 0 014-9z"/></svg>
);

/* ---- Logo ---- */
export const Logo = ({ size = 32 }: { size?: number }) => (
  <svg width={size} height={size} viewBox="0 0 40 40" fill="none">
    <defs>
      <linearGradient id="lk-sunset" x1="0" y1="0" x2="40" y2="40">
        <stop offset="0%" stopColor="#ffb700" />
        <stop offset="50%" stopColor="#ff8c42" />
        <stop offset="100%" stopColor="#e91e63" />
      </linearGradient>
    </defs>
    <rect x="2" y="2" width="36" height="36" rx="10" fill="url(#lk-sunset)" />
    <path d="M12 14h16M12 20h16M12 26h10" stroke="#1a1200" strokeWidth="2.5" strokeLinecap="round" />
    <circle cx="28" cy="26" r="2.5" fill="#1a1200" />
  </svg>
);

export const LogoWordmark = ({ size = 32 }: { size?: number }) => (
  <img
    src="/luigipos-lockup-dark.svg"
    alt="LuigiPOS"
    style={{ height: size, width: "auto", display: "block" }}
  />
);
